import { Component } from '@theme/component';

class SolRefresh extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    const config = this.querySelector('.sol-refresh-menu-config');
    if (config) {
      this.menuCopy = JSON.parse(config.textContent);
      this.menuKind = 'store';
      this.updateMenu();
    }
    this.addEventListener('click', event => {
      const tab = event.target.closest('[data-menu]');
      if (tab) {
        this.menuKind = tab.dataset.menu;
        this.updateMenu();
      }
      if (event.target.closest('.zoom-menu')) {
        const dialog = this.querySelector('#menu-lightbox');
        const textSource = this.querySelector(`.menu-translations > [data-menu-content="${this.menuKind}"]`);
        const largeImage = this.querySelector('#large-menu');
        const largeText = this.querySelector('#large-menu-text');
        if (textSource) {
          largeImage.hidden = true;
          const copy = textSource.cloneNode(true);
          copy.hidden = false;
          largeText.replaceChildren(copy);
          largeText.hidden = false;
        } else {
          const source = this.querySelector('#menu-image');
          largeImage.src = source.dataset.fullSrc || source.src;
          largeImage.alt = source.alt;
          largeImage.hidden = false;
          largeText.hidden = true;
        }
        this.querySelector('#lightbox-caption').textContent = this.menuKind === 'home' ? this.menuCopy.menuHomeLabel : this.menuCopy.menuStoreLabel;
        dialog.showModal();
        const scrollArea = dialog.querySelector('.lightbox-scroll');
        if (scrollArea) { scrollArea.scrollTop = 0; scrollArea.scrollLeft = 0; }
      }
      const close = event.target.closest('[data-close]');
      if (close) close.closest('dialog')?.close();
      if (event.target.closest('#zoom-toggle')) {
        const dialog = this.querySelector('#menu-lightbox');
        const actual = dialog.classList.toggle('actual');
        this.querySelector('#zoom-toggle').textContent = actual ? this.menuCopy.fitSize : this.menuCopy.actualSize;
      }
    }, options);
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', event => {
      const tabs = Array.from(this.querySelectorAll('[role="tab"][data-menu]'));
      const current = tabs.indexOf(event.target);
      if (current < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      this.menuKind = tabs[next].dataset.menu;
      this.updateMenu();
      tabs[next].focus();
    }, options);
    this.querySelectorAll('dialog').forEach(dialog => dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }, options));
  }

  updateMenu() {
    const c = this.menuCopy;
    const image = this.querySelector('#menu-image');
    const translated = this.dataset.translatedMenu === 'true';
    const home = this.menuKind === 'home';
    const en = this.lang === 'en';
    this.querySelectorAll('[data-menu]').forEach(tab => { const selected = tab.dataset.menu === this.menuKind; tab.setAttribute('aria-selected', String(selected)); tab.tabIndex = selected ? 0 : -1; });
    this.querySelector('#menu-panel').setAttribute('aria-labelledby', home ? 'tab-home' : 'tab-store');
    this.querySelector('#menu-category').textContent = home ? c.atHome : c.inStore;
    this.querySelector('.menu-sheet').hidden = translated;
    this.querySelectorAll('.menu-translations > [data-menu-content]').forEach(panel => { panel.hidden = panel.dataset.menuContent !== this.menuKind; });
    if (!translated) {
      const key = `menu${en ? 'En' : 'Zh'}${home ? 'Home' : 'Store'}`;
      image.dataset.fullSrc = this.dataset[key];
      image.srcset = this.dataset[`${key}Srcset`] || '';
      image.src = image.dataset.fullSrc;
      image.alt = home ? c.menuHomeLabel : c.menuStoreLabel;
    }
    const context = this.querySelector('#menu-context');
    context.replaceChildren();
    const heading = document.createElement('h3'); heading.textContent = home ? c.menuHomeHeading : c.menuStoreHeading;
    const paragraph = document.createElement('p'); paragraph.textContent = home ? c.menuHomeText : c.menuStoreText;
    context.append(heading, paragraph);
    if (!home) {
      const list = document.createElement('ul'); list.className = 'menu-highlights';
      ['hairTitle', 'stoneTitle', 'chineseTitle'].forEach(key => {
        const item = document.createElement('li');
        const name = document.createElement('span'); name.textContent = c[key];
        const time = document.createElement('span'); time.textContent = c.menuTime;
        item.append(name, time); list.append(item);
      });
      context.append(list);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    super.disconnectedCallback();
  }
}

if (!customElements.get('sol-refresh')) customElements.define('sol-refresh', SolRefresh);

/** Mobile long-page navigation, with lifecycle cleanup for section rendering. */
class SolBackToTop extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.button = this.querySelector('button');
    this.updateVisibility = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.button) this.button.hidden = window.scrollY < window.innerHeight * 2;
      });
    };
    window.addEventListener('scroll', this.updateVisibility, {passive: true, signal});
    window.addEventListener('resize', this.updateVisibility, {signal});
    this.button?.addEventListener('click', () => {
      window.scrollTo({top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }, {signal});
    this.updateVisibility();
  }
  disconnectedCallback() {
    this.controller?.abort();
    if (this.frame) cancelAnimationFrame(this.frame);
    super.disconnectedCallback();
  }
}
if (!customElements.get('sol-back-to-top')) customElements.define('sol-back-to-top', SolBackToTop);

/** Native scroll rail for the drawer's merchant-selected service images. */
class SolDrawerCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.list = this.querySelector('[data-sol-carousel-track], .menu-drawer__featured-content-list');
    this.items = Array.from(this.list?.children || []);
    this.previous = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.count = this.querySelector('[data-carousel-count], .sol-drawer-carousel-count');
    this.index = 0;
    this.onScroll = () => {
      if (this.touchAnimating || this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.updatePosition(); });
    };
    this.list?.addEventListener('scroll', this.onScroll, {passive: true, signal});
    this.list?.addEventListener('keydown', event => {
      if (event.target !== this.list) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move((event.key === 'ArrowRight') !== rtl ? 1 : -1);
      }
    }, {signal});
    this.previous?.addEventListener('click', () => this.move(-1), {signal});
    this.next?.addEventListener('click', () => this.move(1), {signal});
    if (this.list && this.matches('sol-drawer-carousel')) {
      const track = this.list;
      let drag = null;
      let gestureFrame = null;
      let settleFrame = null;
      let suppressClickUntil = 0;
      const stopFrames = () => {
        if (gestureFrame) cancelAnimationFrame(gestureFrame);
        if (settleFrame) cancelAnimationFrame(settleFrame);
        gestureFrame = settleFrame = null;
      };
      const finish = () => {
        this.touchAnimating = false;
        track.classList.remove('is-swiping');
        this.onScroll();
      };
      const endTouch = event => {
        const ended = drag;
        drag = null;
        stopFrames();
        if (!ended?.horizontal) { finish(); return; }
        track.scrollLeft = ended.pendingLeft;
        suppressClickUntil = performance.now() + 400;
        // Settle once, with snap disabled until the selected card is reached.
        // Restoring snap before a smooth scroll makes WebKit run two adjustments.
        const velocity = performance.now() - ended.lastAt < 90 ? ended.velocity : 0;
        const projection = event?.type === 'touchcancel' ? track.scrollLeft : track.scrollLeft + Math.max(-110, Math.min(110, velocity * 90));
        const target = ended.stops.reduce((best, value) => Math.abs(value - projection) < Math.abs(best - projection) ? value : best, ended.stops[0] ?? 0);
        const from = track.scrollLeft;
        const distance = target - from;
        if (Math.abs(distance) < 1 || matchMedia('(prefers-reduced-motion: reduce)').matches) {
          track.scrollLeft = target;
          finish();
          return;
        }
        const started = performance.now();
        const duration = Math.min(270, 160 + Math.abs(distance) * .3);
        const settle = now => {
          const progress = Math.min(1, (now - started) / duration);
          track.scrollLeft = from + distance * (1 - Math.pow(1 - progress, 3));
          if (progress < 1) settleFrame = requestAnimationFrame(settle);
          else { settleFrame = null; track.scrollLeft = target; finish(); }
        };
        settleFrame = requestAnimationFrame(settle);
      };
      track.addEventListener('touchstart', event => {
        stopFrames();
        this.touchAnimating = false;
        drag = null;
        if (event.touches.length !== 1) { finish(); return; }
        // Keep native snap off when a new finger interrupts the previous settle.
        track.classList.add('is-swiping');
        const touch = event.touches[0];
        const bounds = track.getBoundingClientRect();
        const style = getComputedStyle(track);
        const rtl = style.direction === 'rtl';
        const padding = parseFloat(rtl ? style.paddingRight : style.paddingLeft) || 0;
        const limit = track.scrollWidth - track.clientWidth;
        const left = track.scrollLeft;
        // Geometry is read once per gesture, never in a touchmove frame.
        const stops = this.items.map(item => {
          const rect = item.getBoundingClientRect();
          const value = left + (rtl ? rect.right - bounds.right + padding : rect.left - bounds.left - padding);
          return rtl ? Math.max(-limit, Math.min(0, value)) : Math.max(0, Math.min(limit, value));
        });
        drag = { x: touch.clientX, y: touch.clientY, left, pendingLeft: left, lastLeft: left, lastAt: performance.now(), velocity: 0, horizontal: false, stops, rtl, limit };
      }, {passive: true, signal});
      track.addEventListener('touchmove', event => {
        if (!drag || event.touches.length !== 1) return;
        const touch = event.touches[0];
        const dx = touch.clientX - drag.x;
        const dy = touch.clientY - drag.y;
        if (!drag.horizontal) {
          if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
          if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
          drag.horizontal = true;
          this.touchAnimating = true;
          track.classList.add('is-swiping');
        }
        if (event.cancelable) event.preventDefault();
        const now = performance.now();
        const left = drag.rtl ? Math.max(-drag.limit, Math.min(0, drag.left - dx)) : Math.max(0, Math.min(drag.limit, drag.left - dx));
        drag.velocity = .65 * drag.velocity + .35 * (left - drag.lastLeft) / Math.max(1, now - drag.lastAt);
        drag.lastLeft = drag.pendingLeft = left;
        drag.lastAt = now;
        if (!gestureFrame) gestureFrame = requestAnimationFrame(() => {
          gestureFrame = null;
          if (drag?.horizontal) track.scrollLeft = drag.pendingLeft;
        });
      }, {passive: false, signal});
      track.addEventListener('touchend', endTouch, {passive: true, signal});
      track.addEventListener('touchcancel', endTouch, {passive: true, signal});
      track.addEventListener('click', event => {
        if (performance.now() >= suppressClickUntil) return;
        event.preventDefault();
        event.stopPropagation();
      }, {capture: true, signal});
      this.classList.add('sol-touch-swipe-ready');
      this.touchCleanup = () => {
        stopFrames();
        drag = null;
        this.touchAnimating = false;
        track.classList.remove('is-swiping');
        this.classList.remove('sol-touch-swipe-ready');
      };
    }
    this.closest('details')?.addEventListener('toggle', this.onScroll, {signal});
    this.resizeObserver = new ResizeObserver(this.onScroll);
    if (this.list) this.resizeObserver.observe(this.list);
    this.updatePosition();
  }
  updatePosition() {
    if (!this.list || !this.items.length) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const listRect = this.list.getBoundingClientRect();
    const positions = this.items.map(item => {
      const rect = item.getBoundingClientRect();
      return Math.abs(rtl ? rect.right - listRect.right : rect.left - listRect.left);
    });
    this.index = positions.indexOf(Math.min(...positions));
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.items.length - 1;
    let text = `${this.index + 1} / ${this.items.length}`;
    if (this.matches('sol-photo-carousel')) {
      const visible = this.items.map((item, index) => ({index, rect:item.getBoundingClientRect()}))
        .filter(({rect}) => Math.min(rect.right, listRect.right) - Math.max(rect.left, listRect.left) > rect.width * .5);
      const first = visible[0]?.index ?? this.index;
      const last = visible.at(-1)?.index ?? first;
      if (this.previous) this.previous.disabled = Math.abs(this.list.scrollLeft) < 2;
      if (this.next) this.next.disabled = Math.abs(this.list.scrollLeft) >= this.list.scrollWidth - this.list.clientWidth - 2;
      text = first === last ? `${first + 1} / ${this.items.length}` : `${first + 1}–${last + 1} / ${this.items.length}`;
    }
    if (this.count && this.count.textContent !== text) this.count.textContent = text;
  }
  move(step) {
    const target = this.items[Math.max(0, Math.min(this.items.length - 1, this.index + step))];
    target?.scrollIntoView({block: 'nearest', inline: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.touchCleanup?.();
    if (this.frame) cancelAnimationFrame(this.frame);
    super.disconnectedCallback();
  }
}
if (!customElements.get('sol-drawer-carousel')) customElements.define('sol-drawer-carousel', SolDrawerCarousel);

class SolPhotoCarousel extends SolDrawerCarousel {}
if (!customElements.get('sol-photo-carousel')) customElements.define('sol-photo-carousel', SolPhotoCarousel);
